import {
	Alert,
	Button,
	Col,
	Divider,
	Form,
	Row,
} from "antd";
import React, { useEffect } from "react";
import { getClientFormConfig } from "./configs/clientForm.config";
import { getClientFormPermissions } from "./configs/clientForm.config.permissions";
import { useConfig } from "../../contexts/ConfigContext";
import PageSpinner from "../Spinner/PageSpinner";
import { renderDynamicField } from "../../utils/dynamicFormFieldsUtils";
import { COUNTRY_CODES } from "../../constants/countries";
import { getAperturaActiva } from "../../services/cajaEstadoService";
import db from "../../db/database";

const ClientForm = ({
	initialData,
	loadingForm = false,
	isCreate = false,
	error = "",
	handleSubmit,
	handleCancel,
	handleError,
}) => {
	const [form] = Form.useForm();
	const { country, loading } = useConfig();
	const countryCode = country?.code;
	const { sections } = getClientFormConfig(countryCode);
	const permissions = getClientFormPermissions(countryCode);
	const allFieldsInForm = sections.flatMap(s => s.fields);

	const isReadOnly = !isCreate && !permissions.canUpdate;

	const noValue = Form.useWatch("No", form); // Para Paraguay el No es el Invoice_Disc_Code y es obligatorio pasarlo en la edicion
	const TPVgroupPrice = Form.useWatch("TPV_GroupPrice", form); // Para Paraguay el grupo de precio se obtiene del TPV de la caja abierta

	const buildDynamicClientObject = (values, allFields) => {
		const client = {};

		allFields.forEach((field) => {
			const fieldName = field.name;
			if (values.hasOwnProperty(fieldName)) {
				client[fieldName] = values[fieldName];
			}
			else {
				client[fieldName] = ''; // me aseguro que este vacio
			}
		});

		return client;
	};

	// Precargar datos iniciales cuando se crea/edita un cliente
	useEffect(() => {
		//Carga initialData o empty values para el form
		if (initialData) {
			const clientData = buildDynamicClientObject(initialData, allFieldsInForm);
			form.setFieldsValue({ ...clientData });
		}

		// No sobreescribir País si ya viene precargado
		if (!form.getFieldValue('Pais')) {
			form.setFieldsValue({
				Pais: countryCode,
			})
		}
	}, [initialData, form]);

	useEffect(() => {
		if (countryCode == COUNTRY_CODES.PARAGUAY) {
			const current = form.getFieldValue("Invoice_Disc__Code");
			if (noValue !== current) {
				form.setFieldValue("Invoice_Disc__Code", noValue);
			}
		}
	}, [noValue, form]);

	useEffect(() => {
		let cancelled = false;

		const loadAperturaActiva = async () => {
			if (countryCode !== COUNTRY_CODES.PARAGUAY) return;

			try {
				const apertura = await getAperturaActiva();
				if (cancelled) return;

				const TPV = apertura?.TPV;
				if (!TPV) return;

				const tpvRecord = await db.tpvs
					.where("No")
					.equals(TPV)
					.first();

				const groupPrice = tpvRecord?.customerPriceGroup;
				const currentSalesperson = form.getFieldValue("Salesperson_Code");

				form.setFieldsValue({
					TPV_GroupPrice: groupPrice,
					Customer_Price_Group: groupPrice,
					Salesperson_Code: currentSalesperson || tpvRecord?.cod_Vendedor,
				});
			} catch (err) {
				console.error("Error loading apertura activa", err);
			}
		};

		loadAperturaActiva();

		return () => {
			cancelled = true;
		};
	}, [countryCode, form]);

	return (
		<div className="client-form-container">
			{error && (
				<Alert
					message="Error"
					description={error}
					type="error"
					showIcon
					closable
					onClose={() => handleError("")}
					style={{ marginBottom: 16 }}
				/>
			)}

			<Form
				form={form}
				name="client-form"
				onFinish={(values) => {
					if (isReadOnly) {
						handleCancel(form);
						return;
					}

					const hasChanges = form.isFieldsTouched(true);
					handleSubmit(values, hasChanges);
				}}
				layout="vertical"
				scrollToFirstError
				onKeyDown={(e) => {
					if (isReadOnly && e.key === "Enter") { e.preventDefault(); }
				}}
			>
				{(loading || loadingForm) && <PageSpinner />}
				{!loading && sections ? (
					<>
						{sections.map((section, i) => (
							<React.Fragment key={i}>
								{section.title && (
									<Divider orientation="left">{section.title}</Divider>
								)}
								<Row gutter={16}>
									{section.fields.map((field, j) => (
										<Col xs={24} md={12} key={j}>
											{renderDynamicField(
												field,
												allFieldsInForm,
												form,
												countryCode,
												{ No: noValue, TPV_GroupPrice: TPVgroupPrice }
											)}
										</Col>
									))}
								</Row>
							</React.Fragment>
						))}
					</>
				) : null}

				{!isReadOnly && (
					<Form.Item>
						<div style={{ display: "flex", gap: "10px" }}>
							<Button type="primary" htmlType="submit" loading={loadingForm} block>
								{!isCreate ? "Aceptar" : "Registrar Cliente"}
							</Button>


							<Button onClick={() => { handleCancel(form) }} block>
								Cancelar
							</Button>
						</div>
					</Form.Item>
				)}

			</Form>
		</div>
	);
};

export default ClientForm;
